import { Button, Checkbox, ImportIcon, Select, SelectOptionType, Table, type TableColumns } from '@bp/ui-components';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { niceDate } from '../../../utils/dateCalculations';
import type { ProcuratGroup } from '../hooks/useProcuratGroups';

export type ProcuratClassesTableData = ProcuratGroup & {
  lastImport: string;
  bpClasses: SelectOptionType[];
};

type ProcuratClassesTableProps = {
  data: ProcuratClassesTableData[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onImport: () => void;
  importWithParents: RowSelectionState;
  setImportWithParents: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  bpClassesSelectOpts: SelectOptionType[];
  setBpClasses: (bpClassUuids: string[], procuratId: number) => void;
};

export const ProcuratClassesTable: React.FC<ProcuratClassesTableProps> = ({
  data,
  rowSelection,
  onRowSelectionChange,
  onImport,
  importWithParents,
  setImportWithParents,
  bpClassesSelectOpts,
  setBpClasses,
}) => {
  const { t } = useTranslation();

  const handleCheckBoxChange = (group: ProcuratGroup) => {
    setImportWithParents((prev) => ({
      ...prev,
      [group.id]: !prev[group.id],
    }));
  };

  const groupColumns: TableColumns<ProcuratClassesTableData>[] = [
    {
      header: t('common.name'),
      id: 'name',
      accessorKey: 'name',
    },
    {
      header: t('procuratImport.withParents'),
      id: 'select',
      cell: ({ row }) => (
        <Checkbox
          name={row.id}
          onChange={() => handleCheckBoxChange(row.original)}
          checked={importWithParents[row.original.id] ?? false}
          disabled={!rowSelection[row.id]}
        />
      ),
    },
    {
      id: 'schoolYear',
      accessorKey: 'schoolYear',
      header: t('common.schoolyear'),
      size: 150,
    },
    {
      id: 'lastImport',
      accessorKey: 'lastImport',
      header: t('common.lastUpdated'),
      cell: ({ row }) => (row.original.lastImport ? niceDate(row.original.lastImport, 'short', 'short') : '--'),
    },
    {
      id: 'bpClasses',
      header: t('planungImport.classInBp', { count: 2 }),
      enableSorting: false,
      enableHiding: false,
      enableMultiSort: false,
      enableColumnFilter: false,
      size: 250,
      cell: ({ row }) => {
        const bpClasses = row.original.bpClasses;
        return (
          <Select
            disabled={!rowSelection[row.id]}
            menuPosition={'fixed'}
            isMulti
            dense
            isClearable
            isSearchable
            keepHeight
            value={bpClasses}
            name={'class'}
            onChange={(event) => {
              const selected = event as MultiValue<SelectOptionType>;
              setBpClasses(
                selected.map((s) => s.value as string),
                row.original.id,
              );
            }}
            options={bpClassesSelectOpts}
          />
        );
      },
    },
  ];

  const hasSelectedRows = Object.keys(rowSelection).length > 0;

  return (
    <Table<ProcuratClassesTableData>
      showActionBar
      actionBarSettings={{
        showSelectedRowsCount: true,
        showExpertFilter: false,
        globalFilterPlaceholder: t('common.name'),
        extendedActionsRight: (
          <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onImport} disabled={!hasSelectedRows}>
            {t('procuratImport.importClasses')}
          </Button>
        ),
      }}
      emptyStateSettings={{
        size: 'small',
        hideIcon: true,
        subtitle: t('procuratImport.selectSchoolYearHint'),
      }}
      columns={groupColumns}
      data={data}
      onRowSelectionChange={onRowSelectionChange}
      rowSelection={rowSelection}
      showSelect
      customPadding='var(--spacing-7)'
    />
  );
};
